import React, { useMemo, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useGetAllTagTypes, useGetAllTags } from '@juno/client-api';
import { JunoUser, SearchContentTypeEnum } from '@juno/client-api/model';
import { FeatureConfig as FeatureConfigUtils } from '@juno/client-api/utils';
import {
  Container,
  CreatePrivateMeetingDialog,
  MENTORSHIP_STATUSES,
  UserQuickView,
} from '@juno/ui';
import { getContentRoute, useBreakpoint, useMessengerContext } from '@juno/utils';
import { DirectoryContextProvider, useDirectoryData } from '../Contexts/DirectoryContext';
import { DirectoryFiltersProvider, useDirectoryFilters } from '../Contexts/DirectoryFiltersContext';
import { messengerTabMap } from '../Messenger/index';
import DirectoryFilters from './DirectoryFilters';
import DirectoryFiltersApplied from './DirectoryFiltersApplied';
import DirectoryHeader from './DirectoryHeader';
import FullDirectory from './FullDirectory';
import { RecommendedUsers } from './RecommendedUsers';
import { SavedUsers } from './SavedUsers';
import { DirectoryDataModel } from './form';

const Directory = () => {
  const {
    settings: { site, configs, setShowMessenger, user: currentUser },
    component: { filterTagTypes },
    showMentorshipFilter,
    showTagsFilter,
    showSavedUsers,
    showRecommendedUsers,
  } = useDirectoryData();

  const [filtersOpen, setFiltersOpen] = useState(true);
  const navigate = useNavigate();

  const [selectedUser, setSelectedUser] = useState<JunoUser | null>(null);
  const [newMeetingOpen, setNewMeetingOpen] = useState(false);
  const [userQuickViewAnchorEl, setUserQuickViewAnchorEl] = useState<null | HTMLElement>(null);
  const [quickViewUser, setQuickViewUser] = useState<string | null>(null);
  const { xs } = useBreakpoint();

  const { data: tags, isLoading: isLoadingTags } = useGetAllTags(
    site?.id || '',
    {
      filter: {
        type__in: filterTagTypes?.map((tag) => tag.id),
      },
      order: 'type__value,value',
    },
    {
      query: {
        enabled: showTagsFilter && filterTagTypes?.length > 0,
      },
    },
  );

  const { data: tagTypes, isLoading: isLoadingTagTypes } = useGetAllTagTypes(
    site?.id || '',
    {
      filter: { id__in: filterTagTypes?.map((tag) => tag.id) },
    },
    {
      query: {
        enabled: showTagsFilter && filterTagTypes?.length > 0,
      },
    },
  );

  const { filters } = useDirectoryFilters();

  const tagsFiltersApplied = useMemo(() => {
    if (!filters.tags || !tags) return [];
    const tagIds = filters.tags.map((tagGroup) => tagGroup.tags).flat();
    return tags.filter((tag) => tagIds.includes(tag.id));
  }, [filters.tags, tags]);

  const mentorshipFiltersApplied = useMemo(() => {
    if (!filters.mentorship_status) return [];

    return MENTORSHIP_STATUSES.filter((status) =>
      filters.mentorship_status?.includes(status.value),
    );
  }, [filters.mentorship_status]);

  const messengerDisabled =
    (FeatureConfigUtils.getThemeConfig(configs)?.config?.hide_messenger as boolean) || false;

  const { startConversation, setMessengerTab, setIsConversation, setCurrentChannel } =
    useMessengerContext();

  const startChat = (user: JunoUser) => {
    if (!currentUser) return;
    setShowMessenger && setShowMessenger(true);
    startConversation([user.id, currentUser?.id]).then((channelId) => {
      if (channelId) {
        setMessengerTab(messengerTabMap.Messenger);
        setCurrentChannel(channelId);
        setIsConversation(true);
      }
    });
  };

  const areFiltersAvailable = showTagsFilter || showMentorshipFilter;

  return (
    <Container sx={{ mt: 1, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DirectoryHeader setFiltersOpen={setFiltersOpen} filtersOpen={filtersOpen} />
        </Grid>
        <Grid item xs={12}>
          <DirectoryFiltersApplied
            tagsFiltersApplied={tagsFiltersApplied}
            mentorshipFiltersApplied={mentorshipFiltersApplied}
            loadingData={false}
          />
        </Grid>
        {filtersOpen && areFiltersAvailable && (
          <Grid item sm={3} xs={12}>
            <DirectoryFilters
              tagsFiltersApplied={tagsFiltersApplied}
              mentorshipFiltersApplied={mentorshipFiltersApplied}
              isLoadingTagTypes={isLoadingTagTypes}
              isLoadingTags={isLoadingTags}
              tags={tags || []}
              tagTypes={tagTypes || []}
            />
          </Grid>
        )}
        <Grid item xs={!xs && filtersOpen && areFiltersAvailable ? 9 : 12}>
          {showSavedUsers && (
            <SavedUsers
              messengerDisabled={messengerDisabled}
              setNewMeetingOpen={setNewMeetingOpen}
              startChat={startChat}
              setSelectedUser={setSelectedUser}
            />
          )}
          {showRecommendedUsers && <RecommendedUsers />}

          <FullDirectory
            startChat={startChat}
            setSelectedUser={setSelectedUser}
            setNewMeetingOpen={setNewMeetingOpen}
            messengerDisabled={messengerDisabled}
            setQuickViewUser={setQuickViewUser}
            setUserQuickViewAnchorEl={setUserQuickViewAnchorEl}
          />
        </Grid>

        <CreatePrivateMeetingDialog
          open={newMeetingOpen}
          handleClose={() => setNewMeetingOpen(false)}
          selectedUser={selectedUser}
        />
        <UserQuickView
          anchorEl={userQuickViewAnchorEl}
          userId={quickViewUser || ''}
          open={Boolean(userQuickViewAnchorEl)}
          setAnchorEl={setUserQuickViewAnchorEl}
          onViewProfile={(): void => {
            navigate(
              getContentRoute(site?.slug || '', quickViewUser || '', SearchContentTypeEnum.user),
            );
          }}
          onMessageUser={function (userId: string): void {
            throw new Error('Function not implemented.');
          }}
        />
      </Grid>
    </Container>
  );
};

interface DirectoryModuleProps {
  component: DirectoryDataModel;
}
export const DirectoryModule: React.FC<DirectoryModuleProps> = ({ component }) => {
  if (!component) {
    return null;
  }

  return (
    <DirectoryFiltersProvider>
      {component.title && (
        <Typography variant='h5' p={2}>
          {component.title}
        </Typography>
      )}
      <DirectoryContextProvider component={component}>
        <Directory />
      </DirectoryContextProvider>
    </DirectoryFiltersProvider>
  );
};
